import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mic, Play, Headphones, Radio, ChevronRight, Apple, Music } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const podcastInfo = {
  title: "Dear Cheater Beaters",
  description: "Dear Cheater Beaters is a companion podcast to the TV show that takes you deeper into the lives of characters from the show and beyond. Our hosts talk to interesting people who have cheated and been cheated on as they recount their wild stories.",
  callToAction: "If you are the Cheater, and want to appear on the podcast. Just leave us your Cheating story and we might feature it on our 'Oh No You Didn't' section of the podcast. Tell us how you got caught and the situation and maybe we will give you a call."
};

const fallbackEpisodes = [
  { id: '1', title: "Ep. 50: The Ultimate Betrayal", description: "A story that shocked our entire team. After 20 years of marriage...", duration: "58:32", publishDate: "July 15, 2025" },
  { id: '2', title: "Ep. 49: Double Life Exposed", description: "He had two families in different cities. Neither knew about the other...", duration: "45:18", publishDate: "July 8, 2025" },
  { id: '3', title: "Ep. 48: The Social Media Trail", description: "How a simple Instagram story led to uncovering years of lies...", duration: "52:44", publishDate: "July 1, 2025" },
  { id: '4', title: "Ep. 47: Caught by the Kids", description: "When children accidentally discover what their parents are hiding...", duration: "49:15", publishDate: "June 24, 2025" },
  { id: '5', title: "Ep. 46: The Wedding Crasher", description: "An ex showed up at the wedding with evidence no one expected...", duration: "55:28", publishDate: "June 17, 2025" }
];

const PodcastPage = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      const res = await axios.get(`${API}/podcast/episodes`);
      if (res.data && res.data.length > 0) {
        setEpisodes(res.data);
      } else {
        setEpisodes(fallbackEpisodes);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      setEpisodes(fallbackEpisodes);
    }
    setLoading(false);
  };

  const latestEpisode = episodes[0] || fallbackEpisodes[0];

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Header */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                  <Mic size={24} className="text-white" />
                </div>
                <span className="text-red-400 font-semibold uppercase tracking-wide">Official Podcast</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                Dear <span className="text-red-500">Cheater Beaters</span>
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {podcastInfo.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#"
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <Apple size={20} />
                  Apple Podcasts
                </a>
                <a
                  href="#"
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <Music size={20} />
                  Spotify
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 shadow-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
                    <Headphones size={36} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-2xl">Latest Episode</h3>
                    <p className="text-red-200">Ep. {episodes.length > 0 ? episodes.length + 49 : 50}</p>
                  </div>
                </div>
                <h4 className="text-white font-bold text-xl mb-2">{latestEpisode?.title?.replace(/Ep\. \d+: /, '') || 'The Ultimate Betrayal'}</h4>
                <p className="text-red-100 mb-6">{latestEpisode?.description?.substring(0, 50)}...</p>
                <button className="w-full bg-white text-red-600 py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors">
                  <Play size={20} fill="currentColor" />
                  Listen Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Episodes */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-black text-gray-900">
              All <span className="text-red-600">Episodes</span>
            </h2>
            <div className="flex items-center gap-2 text-gray-500">
              <Radio size={20} />
              <span>Weekly</span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {episodes.map((episode) => (
                <div
                  key={episode.id}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all group cursor-pointer border-l-4 border-transparent hover:border-red-600"
                >
                  <div className="flex items-start gap-4">
                    <button className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors">
                      <Play size={24} className="text-red-600 group-hover:text-white ml-1" fill="currentColor" />
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                          {episode.title}
                        </h3>
                        <span className="text-gray-400 text-sm">{episode.publishDate}</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{episode.description}</p>
                      <span className="text-red-600 text-sm font-medium">{episode.duration}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Want to Be Featured?
          </h2>
          <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
            {podcastInfo.callToAction}
          </p>
          <Link
            to="/submit"
            className="inline-flex items-center gap-2 bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-50 transition-colors shadow-lg"
          >
            Submit Your Story
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PodcastPage;
